import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Board } from '@/entities/board';
import {
  AccessTabs,
  ASSIGNABLE_ROLES,
  InvitePanel,
  LinksPanel,
  type MemberRole,
  RequestsList,
  useBoardAccess,
  useBoardRequests,
  useParticipants,
} from '@/entities/collab';
import { can, PERMISSIONS } from '@/shared/lib/permissions';

import styles from './BoardAccess.module.css';
import { PeopleTab } from './PeopleTab';
import { RolesTab } from './RolesTab';

export type AccessTab = 'people' | 'invite' | 'links' | 'requests' | 'roles';

/** Roles the viewer may hand out: strictly below their own (lead moderators: up to their own). */
function assignableRoles(role: MemberRole, isOwner: boolean): MemberRole[] {
  if (isOwner || role === 'lead_moderator' || role === 'owner') return [...ASSIGNABLE_ROLES];
  const index = ASSIGNABLE_ROLES.indexOf(role);
  return index < 0 ? [] : ASSIGNABLE_ROLES.slice(index + 1);
}

interface Props {
  board: Board;
  role: MemberRole;
  permissions: readonly string[];
  initialTab?: AccessTab;
}

/**
 * THE permissions surface of a board (people, invitations, links, requests, role matrix). The
 * fast access button of the header and the "Access" section of the board settings both render
 * this one component, so there is a single implementation to keep correct. What is shown follows
 * the permissions the server reported; the server enforces every action again. Inherited access
 * (team / workspace rules) is listed but changed where it is defined.
 */
export function BoardAccessPanel({ board, role, permissions, initialTab = 'people' }: Props) {
  const { t } = useTranslation();
  const isOwner = role === 'owner';
  const canMembers = can(permissions, PERMISSIONS.boardManageMembers);
  const canPermissions = can(permissions, PERMISSIONS.boardManagePermissions);
  const admin = canMembers || canPermissions;
  const participants = useParticipants(board.id);
  const access = useBoardAccess(board.id, admin);
  const requests = useBoardRequests(board.id, canMembers);
  const assignable = assignableRoles(role, isOwner);

  const tabs: { id: AccessTab; label: string }[] = [
    { id: 'people', label: t('access.tabs.people') },
    ...(canMembers
      ? [
          { id: 'invite' as const, label: t('access.tabs.invite') },
          { id: 'links' as const, label: t('access.tabs.links') },
          { id: 'requests' as const, label: t('access.tabs.requests') },
        ]
      : []),
    ...(canPermissions ? [{ id: 'roles' as const, label: t('access.tabs.roles') }] : []),
  ];
  const [tab, setTab] = useState<AccessTab>(
    tabs.some((x) => x.id === initialTab) ? initialTab : 'people',
  );

  return (
    <div className={styles.panelRoot}>
      {access.data?.transfer_restricted && isOwner && (
        <p className={styles.warn} role="status">
          {t('access.restricted')}
        </p>
      )}
      <AccessTabs tabs={tabs} value={tab} onChange={setTab} label={t('access.tabsLabel')} />
      {tab === 'people' && (
        <PeopleTab
          boardId={board.id}
          participants={participants.data ?? []}
          access={access.data}
          canManageMembers={canMembers}
          canManagePermissions={canPermissions}
          assignable={assignable}
        />
      )}
      {tab === 'invite' && (
        <InvitePanel scope={{ kind: 'board', id: board.id }} allowedRoles={assignable} />
      )}
      {tab === 'links' && (
        <LinksPanel scope={{ kind: 'board', id: board.id }} allowedRoles={assignable} />
      )}
      {tab === 'requests' && (
        <RequestsList requests={requests.data ?? []} canResolve={canMembers} />
      )}
      {tab === 'roles' && (
        <RolesTab boardId={board.id} ownPermissions={permissions} isOwner={isOwner} />
      )}
    </div>
  );
}
